import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '../hooks/useAuth';
import { AspectRatio } from '../components/ui/aspect-ratio';
import { UserMenu } from '../components/UserMenu';
import { MilesLogo } from '../components/MilesLogo';
import { Button } from '../components/ui/button';
import { PulsingDot } from '../components/ui/pulsing-dot';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Plus,
  Images,
  X,
} from 'lucide-react';
import { API_URL } from '../lib/api';

interface Hero {
  id: string;
  heroId: string;
  slug: string;
  previewUrl: string;
  directionName: string | null;
  isSelected: boolean;
  siteName: string | null;
  siteId: string | null;
  createdAt: string;
}

interface SiteGroup {
  siteId: string;
  siteName: string;
  heroes: Hero[];
}

function groupHeroesBySite(heroes: Hero[]): SiteGroup[] {
  const map = new Map<string, SiteGroup>();
  for (const hero of heroes) {
    const key = hero.siteId || hero.slug;
    if (!map.has(key)) {
      map.set(key, {
        siteId: hero.siteId || key,
        siteName: hero.siteName || 'Unknown Site',
        heroes: [],
      });
    }
    map.get(key)!.heroes.push(hero);
  }
  return Array.from(map.values());
}

function HeroTile({
  hero,
  onClick,
}: {
  hero: Hero;
  onClick: () => void;
}): JSX.Element {
  const previewUrl = `${API_URL}${hero.previewUrl}`;

  return (
    <div
      className="group relative rounded-lg overflow-hidden bg-neutral-800/60 cursor-pointer ring-1 ring-white/[0.06] hover:ring-white/20 transition-all duration-200"
      onClick={onClick}
    >
      <AspectRatio ratio={16 / 9}>
        <div className="relative w-full h-full overflow-hidden">
          <iframe
            src={previewUrl}
            className="absolute inset-0 w-[400%] h-[400%] origin-top-left scale-[0.25] pointer-events-none"
            title={hero.directionName || 'Hero preview'}
            loading="lazy"
          />
        </div>
      </AspectRatio>

      {/* Selected indicator — always visible */}
      {hero.isSelected && (
        <div className="absolute top-1.5 right-1.5 z-10">
          <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center shadow-md">
            <Check className="w-3 h-3 text-white" strokeWidth={3} />
          </div>
        </div>
      )}

      {/* Hover overlay */}
      <div
        className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent
                    opacity-0 group-hover:opacity-100 transition-opacity duration-200
                    flex items-end p-2.5"
      >
        {hero.directionName && (
          <span className="text-xs text-white/90 font-medium truncate">
            {hero.directionName}
          </span>
        )}
      </div>
    </div>
  );
}

function HeroLightbox({
  hero,
  heroes,
  onClose,
  onNavigate,
  onCreateSite,
  isCreating,
}: {
  hero: Hero;
  heroes: Hero[];
  onClose: () => void;
  onNavigate: (hero: Hero) => void;
  onCreateSite: (heroId: string) => void;
  isCreating: boolean;
}): JSX.Element {
  const previewUrl = `${API_URL}${hero.previewUrl}`;
  const currentIndex = heroes.findIndex((h) => h.id === hero.id);
  const prevHero = currentIndex > 0 ? heroes[currentIndex - 1] : null;
  const nextHero =
    currentIndex < heroes.length - 1 ? heroes[currentIndex + 1] : null;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && prevHero) onNavigate(prevHero);
      if (e.key === 'ArrowRight' && nextHero) onNavigate(nextHero);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [prevHero, nextHero, onClose, onNavigate]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Previous arrow */}
      {prevHero && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(prevHero);
          }}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      )}

      {/* Next arrow */}
      {nextHero && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(nextHero);
          }}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        >
          <ArrowRight className="w-5 h-5" />
        </button>
      )}

      {/* Main content */}
      <div
        className="flex flex-col items-center gap-4 max-w-[85vw] w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Large preview */}
        <div className="w-full rounded-xl overflow-hidden ring-1 ring-white/10 bg-neutral-900">
          <AspectRatio ratio={16 / 9}>
            <div className="relative w-full h-full overflow-hidden">
              <iframe
                src={previewUrl}
                className="absolute inset-0 w-[200%] h-[200%] origin-top-left scale-[0.5] pointer-events-none"
                title={hero.directionName || 'Hero preview'}
              />
            </div>
          </AspectRatio>
        </div>

        {/* Info bar */}
        <div className="flex items-center justify-between w-full px-1">
          <div className="flex items-center gap-3 min-w-0">
            {hero.directionName && (
              <span className="text-sm text-white font-medium truncate">
                {hero.directionName}
              </span>
            )}
            {hero.siteName && (
              <span className="text-xs text-white/40 truncate">
                {hero.siteName}
              </span>
            )}
            {hero.isSelected && (
              <span className="inline-flex items-center gap-1 text-[11px] text-green-400 bg-green-500/10 px-2 py-0.5 rounded-full shrink-0">
                <Check className="w-3 h-3" strokeWidth={3} />
                Selected
              </span>
            )}
          </div>
          <Button
            size="sm"
            onClick={() => onCreateSite(hero.heroId)}
            disabled={isCreating}
            className="shrink-0"
          >
            {isCreating ? (
              <span className="relative flex h-3.5 w-3.5 items-center justify-center">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-current/40" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-current/60" />
              </span>
            ) : (
              <Plus className="w-3.5 h-3.5" />
            )}
            Create Site
          </Button>
        </div>
      </div>
    </div>
  );
}

function HeroGalleryHeader({
  user,
  heroCount,
}: {
  user: {
    email?: string | null;
    name?: string | null;
    image?: string | null;
  } | null;
  heroCount: number;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between shrink-0">
      <div className="flex items-center gap-2 text-sm">
        <MilesLogo />
        <span className="text-white/30">/</span>
        <span className="text-white font-medium">Hero Designs</span>
        {heroCount > 0 && (
          <span className="text-xs text-white/30 tabular-nums ml-1">
            {heroCount}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {user && (
          <div className="ml-2">
            <UserMenu user={user} />
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyState(): JSX.Element {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col items-center justify-center h-full gap-4">
      <div className="w-14 h-14 rounded-full bg-white/5 flex items-center justify-center">
        <Images className="w-7 h-7 text-white/40" />
      </div>
      <div className="text-center">
        <p className="text-base font-medium text-white">
          No hero designs yet
        </p>
        <p className="text-sm text-white/60 mt-1 max-w-md">
          Hero designs are generated when you create a site. Each site produces
          unique hero designs to choose from.
        </p>
      </div>
      <Button onClick={() => navigate({ to: '/' })} variant="outline">
        Go to Sites
      </Button>
    </div>
  );
}

export function HeroGalleryPage() {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [heroes, setHeroes] = useState<Hero[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [creatingHeroId, setCreatingHeroId] = useState<string | null>(null);
  const [selectedHero, setSelectedHero] = useState<Hero | null>(null);
  const [error, setError] = useState<string | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate({ to: '/login' });
      return;
    }
    if (!authLoading && isAuthenticated) {
      loadHeroes();
    }
  }, [authLoading, isAuthenticated]);

  const loadHeroes = async (cursor?: string) => {
    try {
      if (cursor) {
        setIsLoadingMore(true);
      }

      const params = new URLSearchParams();
      if (cursor) params.set('cursor', cursor);
      params.set('limit', '100');

      const res = await fetch(
        `${API_URL}/api/v2/cloud/heroes?${params.toString()}`,
        { credentials: 'include' },
      );

      if (!res.ok) throw new Error('Failed to fetch heroes');

      const data = await res.json();

      if (cursor) {
        setHeroes((prev) => [...prev, ...data.heroes]);
      } else {
        setHeroes(data.heroes);
      }
      setNextCursor(data.nextCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleCreateSite = async (heroId: string) => {
    setCreatingHeroId(heroId);
    try {
      const res = await fetch(
        `${API_URL}/api/v2/cloud/heroes/${heroId}/create-site`,
        {
          method: 'POST',
          credentials: 'include',
        },
      );

      if (!res.ok) throw new Error('Failed to create site');

      const data = await res.json();
      navigate({ to: '/sites/$siteId', params: { siteId: data.site.id } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create site');
    } finally {
      setCreatingHeroId(null);
    }
  };

  const flatHeroes = heroes;

  const siteGroups = useMemo(() => groupHeroesBySite(heroes), [heroes]);

  const handleLightboxNavigate = useCallback((hero: Hero) => {
    setSelectedHero(hero);
  }, []);

  if (authLoading || isLoading) {
    return (
      <div className="h-dvh flex flex-col px-4 pb-4 pt-2 gap-2">
        <div className="h-12 shrink-0" />
        <div className="miles-container flex-1 min-h-0 flex items-center justify-center">
          <PulsingDot />
        </div>
      </div>
    );
  }

  return (
    <div className="h-dvh flex flex-col px-4 pb-4 pt-2 gap-2">
      <HeroGalleryHeader user={user} heroCount={heroes.length} />

      <div className="miles-container flex-1 min-h-0 flex flex-col">
        <div className="flex-1 overflow-auto px-6 py-5 scrollbar-thin">
          {error && (
            <div className="mb-4 p-3 rounded-md bg-red-500/10 text-red-400 text-sm">
              {error}
            </div>
          )}

          {heroes.length === 0 && !error ? (
            <EmptyState />
          ) : (
            <div className="space-y-14">
              {siteGroups.map((group) => (
                <div key={group.siteId}>
                  {/* Site group header */}
                  <div className="mb-3">
                    <h3 className="text-lg font-semibold text-white">
                      {group.siteName}
                    </h3>
                  </div>

                  {/* Hero grid — dense 4-column */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5">
                    {group.heroes.map((hero) => (
                      <HeroTile
                        key={hero.id}
                        hero={hero}
                        onClick={() => setSelectedHero(hero)}
                      />
                    ))}
                  </div>
                </div>
              ))}

              {nextCursor && (
                <div ref={loadMoreRef} className="flex justify-center pt-2 pb-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadHeroes(nextCursor)}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? (
                      <span className="relative flex h-3.5 w-3.5 items-center justify-center">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-current/40" />
                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-current/60" />
                      </span>
                    ) : null}
                    Load More
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {selectedHero && (
        <HeroLightbox
          hero={selectedHero}
          heroes={flatHeroes}
          onClose={() => setSelectedHero(null)}
          onNavigate={handleLightboxNavigate}
          onCreateSite={handleCreateSite}
          isCreating={creatingHeroId === selectedHero.heroId}
        />
      )}
    </div>
  );
}
